/**
 * Scroll coordinator for layered tiles on touch screens, where there is no
 * pointer to follow: the page's own scroll drives the layers, and exactly one
 * tile on the whole page — the one nearest the viewport's vertical centre — is
 * popped at a time. That "one at a time" is a page-wide decision, so it lives
 * here rather than in each tile.
 *
 * One IntersectionObserver keeps the measured set to the tiles on screen, and
 * one capture-phase scroll listener covers any scrolling ancestor (the lobby
 * scrolls inside a layout container, not always the window). Both listeners
 * exist only while at least one tile is visible.
 */

export interface ScrollTile {
  el: HTMLElement;
  /** The tile centre's offset from the viewport centre, -1 (top) … 1 (bottom). */
  onOffset: (offset: number) => void;
  onCentred: (centred: boolean) => void;
}

/** How close to the viewport centre a tile has to be to pop, as a share of the viewport height. */
export const CENTRE_BAND = 0.18;

const observed = new Map<Element, ScrollTile>();
const visible = new Set<ScrollTile>();
let centred: ScrollTile | null = null;
let io: IntersectionObserver | null = null;
let frame = 0;

function clamp(v: number): number {
  return Math.max(-1, Math.min(1, v));
}

export function measure(): void {
  frame = 0;
  const vh = window.innerHeight;
  if (vh <= 0) return;
  const mid = vh / 2;
  let best: ScrollTile | null = null;
  let bestDist = Infinity;
  for (const tile of visible) {
    const r = tile.el.getBoundingClientRect();
    const c = r.top + r.height / 2;
    tile.onOffset(clamp((c - mid) / mid));
    const dist = Math.abs(c - mid);
    if (dist < bestDist) {
      bestDist = dist;
      best = tile;
    }
  }
  const next = best && bestDist < vh * CENTRE_BAND ? best : null;
  if (next === centred) return;
  centred?.onCentred(false);
  centred = next;
  centred?.onCentred(true);
}

function schedule(): void {
  if (!frame) frame = requestAnimationFrame(measure);
}

function listen(on: boolean): void {
  const method = on ? 'addEventListener' : 'removeEventListener';
  document[method]('scroll', schedule, { capture: true, passive: true } as AddEventListenerOptions);
  window[method]('resize', schedule, { passive: true } as AddEventListenerOptions);
  if (!on && frame) {
    cancelAnimationFrame(frame);
    frame = 0;
  }
}

function setVisible(tile: ScrollTile, on: boolean): void {
  const before = visible.size;
  if (on) visible.add(tile);
  else visible.delete(tile);
  if (!on && centred === tile) {
    centred = null;
    tile.onCentred(false);
  }
  if (before === 0 && visible.size > 0) listen(true);
  if (before > 0 && visible.size === 0) listen(false);
  schedule();
}

/** Start coordinating a tile; the returned function stops it. */
export function observeScrollTile(tile: ScrollTile): () => void {
  if (typeof IntersectionObserver === 'undefined') {
    setVisible(tile, true);
    return () => setVisible(tile, false);
  }
  io ??= new IntersectionObserver((entries) => {
    for (const entry of entries) {
      const t = observed.get(entry.target);
      if (t) setVisible(t, entry.isIntersecting);
    }
  });
  observed.set(tile.el, tile);
  io.observe(tile.el);
  return () => {
    io?.unobserve(tile.el);
    observed.delete(tile.el);
    if (visible.has(tile)) setVisible(tile, false);
  };
}

/** Re-pick the centred tile on the next frame, e.g. after a press ends. */
export function remeasure(): void {
  schedule();
}
